/* eslint-disable react/no-unescaped-entities */
import {
 Avatar,
 Col,
 Row,
 Form,
 Select,
 Input,
 Upload,
 Button,
 Skeleton,
 Modal,
} from 'antd'
import React, { FC, ReactElement, useState } from 'react'
import Meta from 'antd/lib/card/Meta'
import { globalToaster } from 'components/elements/global'
import { RcFile, UploadFile } from 'antd/lib/upload'
import { utils } from 'utils'
import { BaseButton } from 'components/elements/BaseButton'
import { bytesToMB, mbToBytes } from 'utils/functions'
import { ItemRender } from 'antd/lib/upload/interface'
import { useGetSupportTeamQuery, useSubmitSupportMutation } from '../api'

const UploadItem: ItemRender<any> = (ogNode, file, _f, { remove }) => {
 const { name, size } = file
 return (
  <div className="contact-upload-item">
   <div className="contact-upload-item-r">
    {name}
    <span>Size: {bytesToMB(size ?? 0)}MB</span>
   </div>
   <div className="contact-upload-item-l">
    <Button
     type="link"
     size="small"
     onClick={remove}
     icon={<i className="fas fa-trash" />}
    />
   </div>
  </div>
 )
}

const totalAttachmentMb = (v?: any[]): number =>
 Number(v?.map((i) => Number(i?.size ?? 0))?.reduce((a, b) => a + b, 0) ?? 0)

const ContactUs: FC = () => {
 const [showSuccessModal, setShowSuccess] = useState<any>(null)
 const { TextArea } = Input

 const { data, isLoading } = useGetSupportTeamQuery()
 const [submitSupport, { isLoading: formLoading }] = useSubmitSupportMutation()

 const [form] = Form.useForm<{
  type: string | null
  description: string
  files: { fileList: any[] }
 }>()

 const [fileList, setFileList] = useState([])

 const beforeUpload = (file: RcFile, fList: RcFile[]) => {
  const isSupported = [
   'text/plain',
   'image/jpeg',
   'image/jpg',
   'image/png',
   'application/pdf',
   'application/msword',
   'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
   'text/csv',
   'application/vnd.ms-excel', // Sometimes CSVs are detected as Excel files
  ].includes(file.type)

  if (!isSupported) {
   globalToaster({ content: `${file.name} is Not Supported`, type: 'error' })
   return Upload.LIST_IGNORE
  }
  const totalSizeInBytes = totalAttachmentMb([...fileList, file])
  if (file.size / 1024 / 1024 > 10) {
   globalToaster({
    type: 'error',
    content: `${file.name} (${(file.size / (1024 * 1024)).toFixed(
     2,
    )} MB) exceeds the maximum file size of 10 MB`,
   })
   return Upload.LIST_IGNORE
  }
  if (totalSizeInBytes > mbToBytes(10)) {
   globalToaster({
    type: 'error',
    content: `Total Size ${bytesToMB(
     totalSizeInBytes,
    )} MB) exceeds the maximum file size of 10 MB`,
   })
   return Upload.LIST_IGNORE
  }

  return false
 }
 const handleChange = (info: any) => {
  setFileList(info.fileList)
 }

 const onSubmit = async (): Promise<void> => {
  const totalSizeInBytes = totalAttachmentMb(fileList)
  if (totalSizeInBytes > mbToBytes(10)) {
   globalToaster({
    type: 'error',
    content: `Maximum file size exceeded (${bytesToMB(
     totalSizeInBytes,
    )}MB). Please upload the file(s) with total size less than 10 MB.`,
   })
  } else {
   const { files, ...values } = await form.validateFields()
   const formData = new FormData()
   formData.append('type', values?.type ?? '')
   formData.append('description', values?.description)
   if (fileList?.length) {
    fileList.forEach((i: any, index) => {
     if (i.originFileObj) {
      formData.append(`files[${index}]`, i.originFileObj)
     }
    })
   }
   const resp = await submitSupport(formData).unwrap()
   if (resp.status === 'error') {
    globalToaster({ type: 'error', content: resp.message })
   } else {
    setShowSuccess(resp.message)
   }
   form.resetFields()
   setFileList([])
  }
 }

 return (
  <div className="contact-us-page">
   <Row className="w-100" gutter={[16, 16]}>
    <Col span={24} sm={12} className="left-wrapper">
     <h1>Contact Us</h1>
     <h3>
      Need to get in touch with us? Either fill out the form or Call our Support
      Team
     </h3>
     <div className="items py-3">
      {isLoading
       ? [...Array(2)].map((_, i) => (
          <Meta
           className="py-4 meta-item"
           key={i}
           avatar={<Skeleton.Avatar size={60} shape="circle" />}
           title={<Skeleton.Input className="pl-3 pb-2" size="default" />}
           description={<Skeleton.Input size="default" />}
          />
         ))
       : null}
      {!isLoading &&
       data?.map((item, index) => (
        <Meta
         className="py-4 meta-item"
         key={index}
         avatar={<Avatar size={60} src={item?.photo} />}
         title={item?.name}
         description={
          <>
           <span className="d-block">{item?.designation}</span>
           <span className="d-block">
            <a href={`tel:${item?.phone}`}>{item?.phone}</a>
           </span>
          </>
         }
        />
       ))}
     </div>

     <p style={{ fontSize: '18px' }}>
      <span className="fw-bold">Email Us: </span>
      <a href="mailto:support@agentroof.com"> support@agentroof.com</a>
     </p>
    </Col>
    <Col span={24} sm={12} className="right-wrapper">
     <Form
      initialValues={{ type: null, description: '', files: null }}
      form={form}
      layout="vertical"
      onFinish={onSubmit}
     >
      <Form.Item
       //  required={false
       // requiredMark={false}}
       required={false}
       rules={[{ required: true, message: 'Type is required' }]}
       name="type"
       label={
        <h3 style={{ fontSize: '18px' }}>
         What do you need help with?<span className="text-danger">*</span>{' '}
        </h3>
       }
      >
       <Select
        placeholder="Please select an option"
        options={[
         { value: 'My Website', label: 'My Website' },
         { value: 'My Subscription', label: 'My Subscription' },
         { value: 'My CRM', label: 'My CRM' },
        ]}
       />
      </Form.Item>
      <Form.Item
       rules={[
        { required: true, message: 'Description is required' },
        { min: 3, message: 'Minimum 3 Characters required' },
       ]}
       name="description"
       required={false}
       label={
        <h3 style={{ fontSize: '18px' }}>
         What's your question, comment or Issue?
         <span className="text-danger">*</span>
        </h3>
       }
      >
       <TextArea rows={6} placeholder="What's your question" />
      </Form.Item>
      <Form.Item
       className="upload-form-item"
       name="files"
       label={
        <>
         <h3 style={{ fontSize: '18px' }}>
          Send us a file, document or screenshot
         </h3>
         <p>
          Supported Files: JPG, JPEG, PNG, DOC, DOCX, PDF, TXT, CSV Maximum File
          Size: 10 MB in total
         </p>
        </>
       }
       valuePropName="fileList"
       getValueFromEvent={(e) => e && e.fileList}
      >
       <Upload
        className="contact-upload"
        listType="picture"
        itemRender={UploadItem}
        multiple
        accept="jpg,jpeg,png,doc,docx,pdf,csv"
        beforeUpload={beforeUpload}
        onChange={handleChange}
        fileList={fileList}
       >
        <Button>
         Choose File{' '}
         {!!totalAttachmentMb(fileList) &&
          `(Total Size: ${bytesToMB(totalAttachmentMb(fileList))} MB)`}
        </Button>
       </Upload>
      </Form.Item>
      <div className="d-flex justify-content-end">
       <Button htmlType="submit" type="primary" loading={formLoading}>
        Submit
       </Button>
      </div>
     </Form>
    </Col>
   </Row>

   {!!showSuccessModal && (
    <Modal
     footer={null}
     cancelText={null}
     visible={!!showSuccessModal}
     onCancel={() => setShowSuccess(null)}
    >
     <div style={{ textAlign: 'center' }}>
      <i
       style={{ fontSize: '50px' }}
       className="far fa-check-circle text-success mb-3"
      />
      <h3>{showSuccessModal}</h3>
      <BaseButton onClick={() => setShowSuccess(null)} type="primary">
       Ok
      </BaseButton>
     </div>
    </Modal>
   )}
  </div>
 )
}

export default ContactUs
